import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, ButtonBase } from '@material-ui/core';
import { Check } from '@material-ui/icons';
import { APP_STYLING } from 'constants';
import { getLocalSettingsProxy } from 'utilities';

const useStyles = makeStyles((theme) => {
  return {
    /* Styles applied to the root element. */
    root: {
      height: theme.spacing(3.75), // 30px
      width: theme.spacing(3.75), // 30px
      margin: '7px',
      borderRadius: '50%',
      border: ({ buttonColor }) => `2px solid ${buttonColor.border}`,
      background: ({ buttonColor }) => buttonColor.background,
      outline: ({ hasError }) => (hasError ? `2px solid #D32F2F` : 'none'),
      outlineOffset: '2px',

      '&:hover, &:focus-visible': {
        borderColor: ({ buttonColor }) => buttonColor.border,
        backgroundColor: ({ buttonColor }) => buttonColor.border,
      },

      '&:focus-visible': {
        outline: ({ hasError }) =>
          `2px solid ${hasError ? '#D32F2F' : theme.palette.shelfBlue[800]}`,
      },
    },
  };
});

const ColorButton = forwardRef(
  (
    {
      'aria-label': ariaLabel,
      className,
      color,
      hasError = false,
      isChecked,
      shouldUseActionRef = false,
      ...props
    },
    ref,
  ) => {
    const [localSettings] = useState(getLocalSettingsProxy);
    const buttonColors =
      APP_STYLING.OPTIONS.BUTTON_COLORS[localSettings.appStyling.colorPalette];
    const buttonColor = buttonColors[color];
    const classes = useStyles({ buttonColor, hasError });

    return (
      <ButtonBase
        aria-label={ariaLabel}
        className={clsx(classes.root, className)}
        disableRipple
        aria-pressed={isChecked}
        {...(shouldUseActionRef ? { action: ref } : { ref })}
        {...props}
      >
        {isChecked ? <Check /> : null}
      </ButtonBase>
    );
  },
);
ColorButton.propTypes = {
  'aria-label': PropTypes.string.isRequired,
  /**
   * The color this component should display.
   */
  color: PropTypes.string.isRequired,
  /**
   * Should the button have error state UI
   */
  hasError: PropTypes.bool,
  /**
   * Should this component's check mark icon be displayed.
   */
  isChecked: PropTypes.bool.isRequired,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  // should the ref be attached to the action or ref prop.
  shouldUseActionRef: PropTypes.bool,
};

export default ColorButton;
